import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  /** Número absoluto do primeiro item da página (0-based → mostrado como offset+1). */
  offset: number;
  /** Tamanho da página atual. */
  limit: number;
  /** Total de itens (do servidor). */
  count: number;
  /** Opções de tamanho de página. Default [10, 25, 50, 100]. */
  pageSizeOptions?: number[];
  /** Substantivo singular para o label ("cliente", "custodiário"). */
  itemLabel?: string;
  /** Substantivo plural. Default = `${itemLabel}s`. */
  itemLabelPlural?: string;
  onChange: (next: { offset: number; limit: number }) => void;
}

/**
 * Controle de paginação por offset. Renderiza:
 *  - Info "Mostrando X–Y de Z itens"
 *  - Seletor de tamanho de página
 *  - Botões Anterior / Próxima com número da página atual
 *
 * Some quando `count === 0` (não polui listagem vazia). Sempre volta
 * para offset=0 quando o `limit` muda (senão o usuário poderia cair em
 * página inexistente).
 */
export const Pagination: React.FC<PaginationProps> = ({
  offset,
  limit,
  count,
  pageSizeOptions = [10, 25, 50, 100],
  itemLabel = 'item',
  itemLabelPlural,
  onChange,
}) => {
  if (count === 0) return null;

  const plural = itemLabelPlural ?? `${itemLabel}s`;
  const totalPages = Math.max(1, Math.ceil(count / limit));
  const currentPage = Math.floor(offset / limit) + 1;
  const canPrev = offset > 0;
  const canNext = offset + limit < count;

  const showingFrom = offset + 1;
  const showingTo = Math.min(offset + limit, count);
  const noun = count === 1 ? itemLabel : plural;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-3 text-sm text-gray-600">
      <span>
        Mostrando <strong>{showingFrom}</strong>–<strong>{showingTo}</strong> de{' '}
        <strong>{count}</strong> {noun}
      </span>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Itens por página:</span>
          <select
            value={limit}
            onChange={(e) => onChange({ offset: 0, limit: Number(e.target.value) })}
            className="rounded-md border border-gray-300 py-1 pl-2 pr-6 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onChange({ offset: Math.max(0, offset - limit), limit })}
            disabled={!canPrev}
            aria-label="Página anterior"
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Anterior
          </button>
          <span className="min-w-[80px] text-center text-xs text-gray-500">
            Página {currentPage} de {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onChange({ offset: offset + limit, limit })}
            disabled={!canNext}
            aria-label="Próxima página"
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Próxima
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
